import { fundingHqFetch, getFundingApiErrorMessage, isFundingAuthError } from './fundingApi';
import { uploadedDocumentToFundingPayload } from '../../components/funding/workspace/fundingDocumentTypes';

function agencyQuery(agencyId, extra = {}) {
  const params = new URLSearchParams({ agency_id: agencyId });
  Object.entries(extra).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params.toString();
}

export async function fetchFundingDocuments({ agencyId, fundingClientId, status, category } = {}) {
  const params = new URLSearchParams();
  if (agencyId) params.set('agency_id', agencyId);
  if (fundingClientId) params.set('funding_client_id', fundingClientId);
  if (status) params.set('status', status);
  if (category) params.set('category', category);
  const query = params.toString();
  const data = await fundingHqFetch(`/documents${query ? `?${query}` : ''}`);
  return {
    documents: data.documents ?? [],
    total: data.total ?? (data.documents?.length ?? 0),
  };
}

export async function fetchClientFundingDocuments(clientId, agencyId) {
  const data = await fundingHqFetch(
    `/clients/${encodeURIComponent(clientId)}/documents?${agencyQuery(agencyId)}`,
  );
  return {
    documents: data.documents ?? [],
    total: data.total ?? (data.documents?.length ?? 0),
    documentStatus: data.documentStatus,
  };
}

export async function createFundingDocument(clientId, agencyId, payload) {
  return fundingHqFetch(
    `/clients/${encodeURIComponent(clientId)}/documents?${agencyQuery(agencyId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}

export async function createDefaultFundingChecklist(clientId, agencyId) {
  return fundingHqFetch(
    `/clients/${encodeURIComponent(clientId)}/documents/default-checklist?${agencyQuery(agencyId)}`,
    { method: 'POST' },
  );
}

export async function updateFundingDocument(documentId, agencyId, payload) {
  return fundingHqFetch(
    `/documents/${encodeURIComponent(documentId)}?${agencyQuery(agencyId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteFundingDocument(documentId, agencyId) {
  return fundingHqFetch(
    `/documents/${encodeURIComponent(documentId)}?${agencyQuery(agencyId)}`,
    { method: 'DELETE' },
  );
}

export function emptyFundingDocumentRequest(category = '') {
  return {
    category,
    status: 'Requested',
    notes: '',
    file_name: '',
    file_url: '',
  };
}

export async function saveUploadedDocumentsToClient(clientId, agencyId, uploadedDocuments = []) {
  const docsToSave = uploadedDocuments.filter((doc) => doc.saveToClientProfile !== false);
  const results = [];

  for (const doc of docsToSave) {
    const payload = uploadedDocumentToFundingPayload(doc);
    const saved = await createFundingDocument(clientId, agencyId, payload);
    results.push(saved);
  }

  return results;
}

export async function saveUploadedDocumentsToClientSafe(clientId, agencyId, uploadedDocuments = []) {
  try {
    const saved = await saveUploadedDocumentsToClient(clientId, agencyId, uploadedDocuments);
    return { saved, error: null, authExpired: false };
  } catch (error) {
    return {
      saved: [],
      error: getFundingApiErrorMessage(error, 'Failed to save documents'),
      authExpired: isFundingAuthError(error),
    };
  }
}
